import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Flame, TrendingUp, Users, MapPin, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { API } from "@/config";

const HighProfileRaces2026 = () => {
  const [races, setRaces] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRaces = async () => {
      try {
        const response = await fetch(`${API}/election2026/high-profile-races`);
        const data = await response.json();
        setRaces(data.races || []);
      } catch (error) {
        console.error("Failed to fetch high-profile races:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchRaces();
  }, []);

  if (loading) {
    return (
      <div className="py-12 text-center">
        <div className="animate-spin w-12 h-12 border-4 border-marigold border-t-transparent rounded-full mx-auto" />
      </div>
    );
  }

  if (races.length === 0) return null;

  return (
    <section className="py-16 px-4 bg-gradient-to-b from-peace-blue to-[#001529] relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-gorkhali-red/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-marigold/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-gorkhali-red/20 px-4 py-2 rounded-full mb-4">
            <Flame className="text-gorkhali-red" size={20} />
            <span className="text-gorkhali-red font-bebas tracking-wider text-sm">2026 ELECTION PREVIEW</span>
          </div>
          
          <h2 className="font-bebas text-4xl sm:text-5xl text-white tracking-wider mb-3">
            HOT SEATS
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Key constituencies – who's running from where in 2026
          </p>
        </motion.div>

        {/* Races Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {races.slice(0, 3).map((race, idx) => (
            <RaceCard key={race.id} race={race} delay={idx * 0.1} />
          ))}
        </div>

        {/* View All Button */}
        {races.length > 3 && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Link
              to="/constituencies"
              className="inline-flex items-center gap-2 bg-marigold hover:bg-marigold/80 text-peace-blue px-6 py-3 rounded-full font-bebas text-lg tracking-wider transition-all group"
            >
              VIEW ALL CONSTITUENCIES
              <ChevronRight className="group-hover:translate-x-1 transition-transform" size={20} />
            </Link>
          </motion.div>
        )}
      </div>
    </section>
  );
};

const RaceCard = ({ race, delay }) => {
  const significanceColors = {
    high: "border-gorkhali-red/50 bg-gorkhali-red/5",
    medium: "border-marigold/50 bg-marigold/5",
    low: "border-white/10 bg-white/5"
  };

  const significanceIcons = {
    high: <Flame className="text-gorkhali-red" size={16} />,
    medium: <TrendingUp className="text-marigold" size={16} />,
    low: <Users className="text-gray-400" size={16} />
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay }}
      whileHover={{ y: -5, scale: 1.02 }}
      className={`border-2 rounded-xl p-6 backdrop-blur-md transition-all ${significanceColors[race.significance]}`}
    >
      {/* Location Badge */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-gray-400 text-sm">
          <MapPin size={14} />
          <span className="font-inter">{race.district}, {race.province}</span>
        </div>
        <div className="flex items-center gap-1">
          {significanceIcons[race.significance]}
        </div>
      </div>

      {/* Title */}
      <h3 className="font-bebas text-2xl text-white tracking-wider mb-2">
        {race.title}
      </h3>
      <p className="text-sm text-gray-400 mb-4 line-clamp-2">
        {race.description}
      </p>

      {/* Constituency */}
      <div className="bg-white/5 rounded-lg px-3 py-2 mb-4">
        <p className="text-xs text-gray-500 mb-1">CONSTITUENCY</p>
        <p className="text-marigold font-bebas text-lg tracking-wider">{race.constituency}</p>
      </div>

      {/* Top Candidates */}
      <div className="space-y-3">
        {race.candidates.slice(0, 2).map((candidate, idx) => (
          <div key={idx} className="flex items-center gap-3">
            <div 
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: candidate.party_color }}
            />
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{candidate.name}</p>
              <p className="text-xs text-gray-500 truncate">{candidate.party}</p>
            </div>
            {candidate.is_incumbent && (
              <span className="text-xs bg-marigold/20 text-marigold px-2 py-0.5 rounded-full whitespace-nowrap">
                Incumbent
              </span>
            )}
            {candidate.is_new && (
              <span className="text-xs bg-gorkhali-red/20 text-gorkhali-red px-2 py-0.5 rounded-full whitespace-nowrap">
                New
              </span>
            )}
          </div>
        ))}
        {race.candidates.length > 2 && (
          <p className="text-xs text-gray-500 text-center pt-2">
            +{race.candidates.length - 2} more candidates
          </p>
        )}
      </div>
    </motion.div>
  );
};

export default HighProfileRaces2026;
